"use client";

import dynamic from "next/dynamic";
import { useState, type FocusEvent, type PointerEvent } from "react";

import BirthToolFormShell from "@/components/tool-shell/BirthToolFormShell";

import {
  VARSHPHAL_FIELD_IDS,
  VARSHPHAL_FORM,
  VARSHPHAL_FORM_FRAME,
  VARSHPHAL_FORM_RESERVE,
} from "./varshphal-form";
import VarshphalShows from "./VarshphalShows";

// The /varshphal/ chart boundary (the #2460 pattern): the server page imports
// only this module, and it owns the one dynamic() to the island. The form,
// place search, varshphal client, verdict and the North/South chart load as
// an async chunk; the first load gains only this file, the shell and the
// "what the chart shows" block.
//
// Activation is element-scoped and local: no window listener and no timer, so
// a reader scrolling the article never downloads the island and the deploy's
// body pointerdown smoke loads nothing; a press, focus or submit on the shell
// does.
//
// Nothing that arrives without a press moves the article below: the slot holds
// two reserved heights, measured with Playwright from 320 to 1280 px wide.
// - The form reserve, from the server HTML (VARSHPHAL_FORM_RESERVE: 890 px on
//   phones, 740 from sm; measured 2026-10-09 on next dev). The form box's
//   floor is its tallest measured state, a three-line location error under the
//   place hint (612 px at 320, 574 at 360, 482 from 640), so the hint or an
//   error fills the room above the action; the "what the chart shows" block
//   (245 px at 320) sits under it. This covers the shell -> form swap after
//   the async chunk loads.
// - The verdict reserve, added by the island inside the submit itself, before
//   the request goes out (VARSHPHAL_VERDICT_RESERVE: 1840 px on phones, 1630
//   from sm, 1060 from md). Re-measured 2026-10-09 on a production build (next
//   build, next start) with POST /varshphal mocked by four replies: the worst
//   case (the longest zone name, "Sept" dates, Vrishchika everywhere a sign is
//   spelled out, all four birth-time flips), a two-flip reply with that zone,
//   the longest strings (a Dhanu lagna, the Muntha's lord Mercury in the 12th
//   house, age 96, four flips) and the contract's Delhi example with a
//   two-flip next varsha. In every state (the verdict; a retrograde graha
//   selected; the next varsha pending, failed, or shown with that graha still
//   selected; back to this year; the South chart), the tallest verdict is 1817
//   at 320, 1814 at 360, 1742-1771 at 390-639, 1609 at 640-700, 1571 at 767,
//   1040 at 768, 1007 at 800 and 975 from 900. A failed next varsha adds no
//   height: its error fills a row the verdict always lays out (VarshphalVerdict).
//   The growth happens in the press that sends the request, an input-driven
//   change (hadRecentInput), not a layout shift; the reply, an error, the next
//   varsha and the North/South toggle all land inside it, and only "Check
//   another birth" releases it. A static verdict reserve would have left about
//   1,000 px of blank under the form on phones for every reader who never
//   casts a chart.
// With those reserves, a PerformanceObserver over the whole flow (every reply
// above, the next varsha answering or failing 1.5 s after its press) saw the
// article below never move after the submit press, and no unexcused shift when
// the next varsha fails. The swap to the next varsha's chart itself redraws the
// chart and its table in place (up to 0.005 inside the verdict, a second after
// the press). translate="no" covers the shell, form and verdict, which
// re-render on "Check another birth" (tests/translate-safe-islands.test.ts).
const SHELL_PROPS = { ...VARSHPHAL_FORM, className: VARSHPHAL_FORM_FRAME } as const;

function ShellState({ onActivate }: { onActivate?: () => void }) {
  return (
    <>
      <BirthToolFormShell {...SHELL_PROPS} onActivate={onActivate} />
      <VarshphalShows />
    </>
  );
}

const VarshphalCheck = dynamic(() => import("./VarshphalCheck"), {
  ssr: false,
  loading: () => <ShellState />,
});

/** BirthToolFormShell input names -> the real form's input ids. */
const SHELL_FIELD_IDS: Readonly<Record<string, string>> = {
  dob: VARSHPHAL_FIELD_IDS.date,
  tob: VARSHPHAL_FIELD_IDS.time,
  place: VARSHPHAL_FIELD_IDS.place,
};

function formFieldId(target: EventTarget | null): string {
  const name = target instanceof Element ? target.getAttribute("name") : null;
  return (name && SHELL_FIELD_IDS[name]) || VARSHPHAL_FIELD_IDS.date;
}

export default function VarshphalCheckLazy() {
  const [active, setActive] = useState(false);
  const [focusFieldId, setFocusFieldId] = useState<string | null>(null);

  // The shell's own onActivate cannot tell a keyboard focus from a touch, so
  // the slot reads the intent in the capture phase: focus follows the visitor
  // into the real form after a keyboard focus, a mouse press or a submit, and
  // never after a touch, which may be the start of a scroll.
  const shellIntent = active
    ? {}
    : {
        onPointerDownCapture: (event: PointerEvent<HTMLDivElement>) =>
          setFocusFieldId(event.pointerType === "mouse" ? formFieldId(event.target) : null),
        onFocusCapture: (event: FocusEvent<HTMLDivElement>) =>
          setFocusFieldId(formFieldId(event.target)),
        onSubmitCapture: () => setFocusFieldId(VARSHPHAL_FIELD_IDS.date),
      };

  return (
    <div
      translate="no"
      data-varshphal-slot=""
      className={`flex flex-col ${VARSHPHAL_FORM_RESERVE}`}
      {...shellIntent}
    >
      {active ? (
        <VarshphalCheck focusFieldId={focusFieldId} />
      ) : (
        <ShellState onActivate={() => setActive(true)} />
      )}
    </div>
  );
}
